import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { attachTilt } from '../../../hooks/useTiltCards';
import { whereWeInvest } from '../../../mocks/libertySoft';

gsap.registerPlugin(ScrollTrigger);

function PillarRow({ pillar, index }: { pillar: typeof whereWeInvest[0]; index: number }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const isLeft = pillar.direction === 'left';

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        el.querySelectorAll('.pillar-row-content'),
        { y: 60, opacity: 0, filter: 'blur(16px)' },
        {
          y: 0,
          opacity: 1,
          filter: 'blur(0px)',
          duration: 1.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 78%' },
        }
      );

      const img = el.querySelector<HTMLElement>('.pillar-row-image');
      if (img) {
        gsap.fromTo(
          img,
          { yPercent: -6 },
          {
            yPercent: 6,
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          }
        );
      }
    }, el);
    const cleanupTilt = attachTilt(el, 3);
    return () => {
      ctx.revert();
      cleanupTilt();
    };
  }, []);

  return (
    <div ref={ref} className={`relative flex flex-col ${isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-stretch min-h-[520px] lg:min-h-[580px]`}>
      {/* Image side */}
      <div className="relative w-full lg:w-1/2 overflow-hidden border border-white/5 min-h-[300px] md:min-h-[400px]">
        <img
          src={pillar.image}
          alt={pillar.title}
          className="pillar-row-image absolute inset-0 w-full h-[115%] object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      </div>

      {/* Content side */}
      <div className="relative w-full lg:w-1/2 flex items-center bg-background-100">
        <div className={`p-10 md:p-14 lg:p-16 w-full ${isLeft ? 'lg:pl-20' : 'lg:pr-20'}`}>
          <h3 className="pillar-row-content font-display text-3xl md:text-4xl lg:text-5xl mb-6 leading-[1.08] cursor-default">
            <span className="title-shimmer inline-block">{pillar.title}</span>
          </h3>

          <p className="pillar-row-content text-white/55 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
            {pillar.description}
          </p>

          {/* Bottom line accent */}
          <div className="pillar-row-content absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary-400/30 to-transparent" />
        </div>
      </div>
    </div>
  );
}

export default function PillarShowcase() {
  return (
    <section id="investments" className="relative bg-background-50 text-white">
      {/* Full-width pillar rows */}
      <div className="max-w-[1600px] mx-auto border-t border-white/5">
        {whereWeInvest.map((pillar, index) => (
          <div key={pillar.num} className="border-b border-white/5">
            <PillarRow pillar={pillar} index={index} />
          </div>
        ))}
      </div>
    </section>
  );
}